import { OpenInNew } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { createPortal } from 'react-dom';

interface IPropsPecaDrawer {
    pecaBodyUrl: string;
    pecaOpened: boolean;
    closePeca: () => void;
    isMinuta?: boolean;
    viewerType?: 'pdf' | 'html';
}

interface IState {
    width: number;
    resizing: boolean;
}

export default class PecaDrawer extends React.PureComponent<IPropsPecaDrawer, IState> {
    drawerRef = React.createRef<HTMLDivElement>();

    constructor(props: IPropsPecaDrawer) {
        super(props);
        this.state = { width: 800, resizing: false };
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.props.closePeca();
        }
    };

    handleClickOutside = (e: MouseEvent) => {
        const drawer = this.drawerRef.current;
        if (drawer && !drawer.contains(e.target as Node)) {
            this.props.closePeca();
        }
    };

    handleClickAbrirNovaJanela = () => {
        window.open(this.props.pecaBodyUrl, '_blank');
    };

    startResize = (e: React.PointerEvent) => {
        e.preventDefault();
        document.body.style.userSelect = 'none';
        this.setState({ resizing: true });
        window.addEventListener('pointermove', this.handleResize);
        window.addEventListener('pointerup', this.stopResize);
        window.addEventListener('pointercancel', this.stopResize);
    };

    stopResize = () => {
        document.body.style.userSelect = 'auto';
        this.setState({ resizing: false });
        window.removeEventListener('pointermove', this.handleResize);
        window.removeEventListener('pointerup', this.stopResize);
        window.removeEventListener('pointercancel', this.stopResize);
    };

    handleResize = (e: PointerEvent) => {
        if (this.state.resizing) {
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > 400 && newWidth < 1600) {
                this.setState({ width: newWidth });
            }
        }
    };

    render() {
        const { pecaOpened, closePeca, pecaBodyUrl, isMinuta = false, viewerType = 'pdf' } = this.props;
        const { width, resizing } = this.state;
        const tooltipAbrirNovaJanela = isMinuta
            ? 'Abrir minuta em nova janela'
            : 'Abrir peça em nova janela';

        if (!pecaOpened) return null;

        const drawerContent = (
            <>
                {resizing && (
                    <Box
                        onPointerUp={this.stopResize}
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 2000,
                            cursor: 'ew-resize',
                            backgroundColor: 'transparent',
                        }}
                    />
                )}

                {/* Overlay clicável para fechar ao clicar fora */}
                <Box
                    onClick={closePeca}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1200,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    }}
                />

                <Box
                    ref={this.drawerRef}
                    sx={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        height: '100vh',
                        width: `${width}px`,
                        bgcolor: 'background.paper',
                        boxShadow: '-2px 0 10px rgba(0,0,0,0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 1300,
                        borderLeft: 'none',
                        overflow: 'hidden',
                        transition: resizing ? 'none' : 'width 0.15s ease',
                    }}
                >
                    <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 70,
                                left: 15,
                                display: 'flex',
                                gap: 1,
                                backgroundColor: 'rgba(133, 131, 131, 0.85)',
                                borderRadius: '8px',
                                padding: '4px',
                                zIndex: 10,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                transition: 'opacity 0.2s ease',
                                opacity: 0.3,
                                '&:hover': {
                                    opacity: 1,
                                },
                            }}
                        >
                            <Tooltip title={tooltipAbrirNovaJanela}>
                                <IconButton size="small" onClick={this.handleClickAbrirNovaJanela}>
                                    <OpenInNew fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Fechar painel'>
                                <IconButton size="small" onClick={closePeca}>
                                    ✖
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {viewerType === 'html' ? (
                            <iframe
                                src={pecaBodyUrl}
                                title='Visualização de minuta'
                                width='100%'
                                height='100%'
                                style={{ border: 'none', display: 'block' }}
                            />
                        ) : (
                            <object
                                key={Math.round(width / 100)}
                                data={`${pecaBodyUrl}#navpanes=0&zoom=page-width`}
                                type='application/pdf'
                                width='100%'
                                height='100%'
                                style={{ display: 'block' }}
                            >
                                Seu navegador não suporta visualização de PDF.
                            </object>
                        )}
                    </Box>
                    <Box
                        onPointerDown={this.startResize}
                        sx={{
                            position: 'absolute',
                            left: '-3px',
                            top: 0,
                            width: '6px',
                            height: '100%',
                            cursor: 'ew-resize',
                            zIndex: 1400,
                        }}
                    />
                </Box>
            </>
        );

        return createPortal(drawerContent, document.body);
    }
}
